/* eslint-disable max-lines */
import type {
    ChangeEvent,
    ReactElement,
    ReactNode,
    SyntheticEvent,
} from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import type {
    GetMaterialOnTheFlyOptionsQuery,
    UnitOfMeasurement,
} from '../../gql';

import { useQuery } from '@apollo/client/react';
import { HighlightText } from '@bestseller-bit/frontend-community.components.highlight-text';
import Autocomplete, {
    type AutocompleteChangeReason,
} from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { debounce } from 'lodash';

import { GetMaterialOnTheFlyOptionsDocument } from '../../gql';
import { GraphClientNames } from '../../types';
import { isDefined } from '../../utils';

import { MaterialOnTheFlyValueContext } from './reducer';

type RowType = UnitOfMeasurement & { id: string };

// Weight unit is not allowed for MOF
const weightUnitErrorMessage = 'Weight unit is not available';

// Weight measure is not allowed for MOF
const weightMeasureErrorMessage = 'Weight unit of measurement is not available';

/** Helper function to convert data for weight measures or weight units to rows */
const convertDataToRows = (
    data: GetMaterialOnTheFlyOptionsQuery['materialOnTheFlyOptions'][
        | 'weightMeasures'
        | 'weightUnits'] = []
): Array<RowType> =>
    data
        // Filter out undefined values
        .filter(isDefined)
        // Sort them based on name
        .toSorted((a, b): number => {
            if (a.unitOfMeasurementName && b.unitOfMeasurementName)
                return a.unitOfMeasurementName.localeCompare(
                    b.unitOfMeasurementName
                );
            return 0;
        })
        // Include an id attribute in each object
        .map((wm) => ({ ...wm, id: wm.unitOfMeasurementPublicId }));

const componentsProps = {
    paper: {
        sx: {
            zIndex: 9999,
            overflow: 'hidden',
        },
    },
};

interface WeightSectionProps {
    isRequired: boolean;
}

const WeightSection = ({ isRequired }: WeightSectionProps): ReactElement => {
    const { materialOnTheFlyValue, dispatchMaterialOnTheFly } = useContext(
        MaterialOnTheFlyValueContext
    );

    const [weightInputValue, setWeightInputValue] = useState<string>(
        String(materialOnTheFlyValue.weightValue ?? '')
    );
    const [weightUnitRows, setWeightUnitRows] = useState<Array<RowType>>([]);
    const [weightMeasureRows, setWeightMeasureRows] = useState<Array<RowType>>(
        []
    );

    const isWeightFilled =
        Boolean(materialOnTheFlyValue.weightUnit) &&
        Boolean(materialOnTheFlyValue.weightMeasure) &&
        materialOnTheFlyValue.weightValue !== null;

    // Validation logic for weight units and measures
    const { isWeightUnitValid, isWeightMeasureValid } = useMemo(() => {
        const currentWeightUnit = materialOnTheFlyValue.weightUnit;
        const currentWeightMeasure = materialOnTheFlyValue.weightMeasure;

        // If no unit/measure is selected, it's valid (not required to have one)
        const isUnitValid =
            !currentWeightUnit ||
            weightUnitRows.some(
                (row) =>
                    row.unitOfMeasurementPublicId ===
                    currentWeightUnit.unitOfMeasurementPublicId
            );

        const isMeasureValid =
            !currentWeightMeasure ||
            weightMeasureRows.some(
                (row) =>
                    row.unitOfMeasurementPublicId ===
                    currentWeightMeasure.unitOfMeasurementPublicId
            );

        return {
            isWeightUnitValid: isUnitValid,
            isWeightMeasureValid: isMeasureValid,
        };
    }, [
        materialOnTheFlyValue.weightUnit,
        materialOnTheFlyValue.weightMeasure,
        weightUnitRows,
        weightMeasureRows,
    ]);

    const { data, loading: isLoading } = useQuery(
        GetMaterialOnTheFlyOptionsDocument, // Query material on the fly options
        {
            fetchPolicy: 'cache-and-network',
            context: {
                clientName: GraphClientNames['bestone-bi4-buying-bom-bff'],
            },
            skip: !materialOnTheFlyValue.materialSubcategory
                ?.materialSubcategoryPublicId,
            variables: {
                materialSubcategoryPublicId:
                    materialOnTheFlyValue.materialSubcategory
                        ?.materialSubcategoryPublicId ?? '',
            },
        }
    );

    useEffect(() => {
        if (data) {
            const newWeightUnitRows = convertDataToRows(
                data.materialOnTheFlyOptions.weightUnits
            );
            const newWeightMeasureRows = convertDataToRows(
                data.materialOnTheFlyOptions.weightMeasures
            );

            // Prefill weight fields only if they are not already set
            const newWeightUnit =
                materialOnTheFlyValue.weightUnit ??
                (isRequired && newWeightUnitRows.length === 1
                    ? newWeightUnitRows[0]
                    : null);
            const newWeightMeasure =
                materialOnTheFlyValue.weightMeasure ??
                (isRequired && newWeightMeasureRows.length === 1
                    ? newWeightMeasureRows[0]
                    : null);

            setWeightInputValue(
                String(materialOnTheFlyValue.weightValue ?? '')
            );
            dispatchMaterialOnTheFly({
                type: 'update_weight_unit',
                payload: { newWeightUnit },
            });
            dispatchMaterialOnTheFly({
                type: 'update_weight_measure',
                payload: { newWeightMeasure },
            });
            // Last but not least, we update the rows for the dropdowns
            setWeightUnitRows(newWeightUnitRows);
            setWeightMeasureRows(newWeightMeasureRows);
        }
    }, [
        data,
        dispatchMaterialOnTheFly,
        isRequired,
        materialOnTheFlyValue.weightMeasure,
        materialOnTheFlyValue.weightUnit,
        materialOnTheFlyValue.weightValue,
    ]);

    const handleUnitChange = useCallback(
        (newWeightUnit: UnitOfMeasurement | null): void => {
            dispatchMaterialOnTheFly({
                type: 'update_weight_unit',
                payload: { newWeightUnit },
            });
        },
        [dispatchMaterialOnTheFly]
    );

    const handleMeasureChange = useCallback(
        (newWeightMeasure: UnitOfMeasurement | null): void => {
            dispatchMaterialOnTheFly({
                type: 'update_weight_measure',
                payload: { newWeightMeasure },
            });
        },
        [dispatchMaterialOnTheFly]
    );

    const handleAmountBlur = (): void => {
        let newWeightInputValue = weightInputValue
            ? Number(weightInputValue)
            : null;
        if (newWeightInputValue && newWeightInputValue < 0) {
            newWeightInputValue = newWeightInputValue * -1;
            setWeightInputValue(String(newWeightInputValue));
        }
        dispatchMaterialOnTheFly({
            type: 'update_weight_value',
            payload: { newWeightValue: newWeightInputValue },
        });
    };

    const handleDebounceFn = useCallback(
        (newValue: string): void => {
            const parsedValue = parseFloat(newValue.replace(',', '.'));
            if (!isNaN(parsedValue)) {
                dispatchMaterialOnTheFly({
                    type: 'update_weight_value',
                    payload: {
                        newWeightValue: parsedValue === 0 ? null : parsedValue,
                    },
                });
            }
        },
        [dispatchMaterialOnTheFly]
    );

    const debounceFn = useMemo(
        () =>
            debounce((newValue: string) => {
                handleDebounceFn(newValue);
            }, 1000),
        [handleDebounceFn]
    );

    const handleAmountChange = (event: ChangeEvent<HTMLInputElement>): void => {
        // Replace comma with dot
        const rawValue = event.target.value.replace(',', '.');

        // Only allow digits and a single dot
        const isValidFormat = rawValue === '' || /^\d+(\.\d*)?$/.test(rawValue);

        // Enforce max length = 7 characters
        const isValidLength = rawValue.length <= 7;

        // Convert to number for limit check
        const parsedValue = parseFloat(rawValue);
        const isUnderLimit = isNaN(parsedValue) || parsedValue <= 999.999;

        if (isValidFormat && isValidLength && isUnderLimit) {
            setWeightInputValue(rawValue);
            debounceFn(rawValue);
        }
    };

    const handleUnitSelect = (
        _event: SyntheticEvent,
        value: UnitOfMeasurement | null,
        reason: AutocompleteChangeReason
    ): void => {
        handleUnitChange(reason === 'clear' ? null : value);
    };

    const handleMeasureSelect = (
        _event: SyntheticEvent,
        value: UnitOfMeasurement | null,
        reason: AutocompleteChangeReason
    ): void => {
        handleMeasureChange(reason === 'clear' ? null : value);
    };

    return (
        <Stack
            sx={{
                width: 1,
            }}
        >
            <FormLabel
                filled={isWeightFilled}
                required={isRequired}
            >
                Weight
            </FormLabel>
            <Stack
                direction="row"
                spacing={10}
                sx={{
                    alignItems: 'center',
                }}
            >
                <TextField
                    placeholder="Enter amount"
                    type="text"
                    value={weightInputValue}
                    disabled={!materialOnTheFlyValue.materialSubcategory}
                    sx={{
                        flex: 1,
                        /* Chrome, Safari, Edge, Opera */
                        [`
                        input::-webkit-outer-spin-button,
                        input::-webkit-inner-spin-button
                        `]: {
                            ['-webkit-appearance']: 'none',
                            margin: 0,
                        },

                        /* Firefox */
                        ['input[type=number]']: {
                            ['-moz-appearance']: 'textfield',
                        },
                    }}
                    onBlur={handleAmountBlur}
                    onChange={handleAmountChange}
                />
                <Stack
                    direction="row"
                    spacing={3}
                    sx={{
                        flex: 1,
                        alignItems: 'center',
                    }}
                >
                    <Autocomplete
                        id="bom-card-mof-weight-unit-select"
                        options={weightUnitRows}
                        loading={isLoading}
                        getOptionLabel={(option): string => option.symbol}
                        noOptionsText="No units found"
                        value={materialOnTheFlyValue.weightUnit}
                        disabled={!materialOnTheFlyValue.materialSubcategory}
                        renderOption={({ key, ...props }, option, state) => (
                            <li
                                key={key}
                                {...props}
                            >
                                <HighlightText
                                    text={option.symbol}
                                    highlight={state.inputValue}
                                />
                            </li>
                        )}
                        renderInput={(params): ReactNode => (
                            <TextField
                                {...params}
                                placeholder=""
                                error={!isWeightUnitValid}
                                fullWidth
                            />
                        )}
                        slotProps={componentsProps}
                        onChange={handleUnitSelect}
                    />
                    <Typography
                        sx={{
                            color: 'text.secondary',
                        }}
                    >
                        /
                    </Typography>
                    <Autocomplete
                        id="bom-card-mof-weight-measure-select"
                        options={weightMeasureRows}
                        loading={isLoading}
                        getOptionLabel={(option): string => option.symbol}
                        noOptionsText="No measures found"
                        value={materialOnTheFlyValue.weightMeasure}
                        disabled={!materialOnTheFlyValue.materialSubcategory}
                        renderOption={({ key, ...props }, option, state) => (
                            <li
                                key={key}
                                {...props}
                            >
                                <HighlightText
                                    text={option.symbol}
                                    highlight={state.inputValue}
                                />
                            </li>
                        )}
                        renderInput={(params): ReactNode => (
                            <TextField
                                {...params}
                                placeholder=""
                                error={!isWeightMeasureValid}
                                fullWidth
                            />
                        )}
                        slotProps={componentsProps}
                        onChange={handleMeasureSelect}
                    />
                </Stack>
            </Stack>
            {(!isWeightUnitValid || !isWeightMeasureValid) && (
                <Stack
                    direction="row"
                    spacing={10}
                    sx={{ mt: 0.5 }}
                >
                    <FormHelperText sx={{ flex: 1 }} />
                    <Stack
                        direction="row"
                        spacing={3}
                        sx={{
                            flex: 1,
                        }}
                    >
                        <FormHelperText
                            sx={{
                                flex: 1,
                                visibility: isWeightUnitValid
                                    ? 'hidden'
                                    : 'visible',
                            }}
                            error
                        >
                            {weightUnitErrorMessage}
                        </FormHelperText>
                        <Box sx={{ width: '8px' }} />
                        <FormHelperText
                            sx={{
                                flex: 1,
                                visibility: isWeightMeasureValid
                                    ? 'hidden'
                                    : 'visible',
                            }}
                            error
                        >
                            {weightMeasureErrorMessage}
                        </FormHelperText>
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
};

export { WeightSection };
